import { _isNil } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils.ts';
import type {
  DataTableColumnType,
  DataTableMetadataGlossaryFieldType,
  DataTableValidatorFunction,
  DataTableValidatorResponse,
} from '../DataTable.model';

const numericValidator: DataTableValidatorFunction = (value) => {
  if (_isNil(value)) {
    return { valid: true };
  }
  const valueAsNumber = Number(value);
  if (isNaN(valueAsNumber)) {
    return { valid: false, error: `[${value}] is not a valid number` };
  }
  if (!Number.isFinite(valueAsNumber)) {
    return { valid: false, error: `[${value}] is not finite` };
  }
  return { valid: true };
};

const textValidator: DataTableValidatorFunction = (value) => {
  if ((value?.length ?? 0) > 255) {
    return { valid: false, error: 'Max length is 255 characters' };
  }
  return { valid: true };
};

const timestampValidator: DataTableValidatorFunction = (value) => {
  if (_isNil(value)) {
    return { valid: true };
  }
  if (!DateUtils.isValidDateTime(value)) {
    return { valid: false, error: `[${value}] is not a valid timestamp` };
  }
  return { valid: true };
};

export class DataTableValidator {
  static readonly #validators: Record<DataTableColumnType, DataTableValidatorFunction> = {
    number: numericValidator,
    measurement: numericValidator,
    formula: (value) => ({ valid: true }),
    observation: (value) => ({ valid: true }),
    text: textValidator,
    timestamp: timestampValidator,
    timestampBaseline: timestampValidator,
    timestampBaselineRelative: (value) => {
      return { valid: true };
    },
    sampleCollectedAt: (value) => {
      if (_isNil(value)) {
        return { valid: true };
      }
      // Allow -1 as special sentinel value for "collect now" action
      if (value === '-1') {
        return { valid: true };
      }
      if (!DateUtils.isValidDateTime(value)) {
        return { valid: false, error: `[${value}] is not a valid timestamp` };
      }
      return { valid: true };
    },
    sample: (value) => ({ valid: true }),
    sampleId: (value) => ({ valid: true }),
    sampleStatus: (value) => ({ valid: true }),
    sampleDoseTime: timestampValidator,
    samplePlannedAt: (value) => ({ valid: true }),
    sampleDetail: numericValidator,
    sampleComments: (value) => ({ valid: true }),
    metadata: (value, metadata) => {
      switch (metadata?.field_type) {
        case 'numeric':
          return numericValidator(value);
        case 'text':
          return textValidator(value);
        case 'date':
          return timestampValidator(value);
        default:
          return { valid: true };
      }
    },
  };

  public static validate(
    type: DataTableColumnType | undefined,
    value: string | null,
    metadata?: { field_type?: DataTableMetadataGlossaryFieldType }
  ): DataTableValidatorResponse {
    if (_isNil(type)) {
      return { valid: false, error: 'invalid column type' };
    }
    return (
      DataTableValidator.#validators?.[type]?.(value, metadata) ?? {
        valid: false,
        error: `[${type}] is not a recognised column type`,
      }
    );
  }
}
